<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import Chevron34 from '/svg/chevron-34.svg'

const props = defineProps<{
  currentPage: number
  itemsPerPage: number
  totalItems: number
  isAdmin?: boolean
  appearance?: string
}>()

const emit = defineEmits(['update:currentPage'])
const router = useRouter()

const FA_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']

const fa = (input: string | number) => {
  return String(input).replace(/[0-9]/g, (d) => FA_DIGITS[Number(d)])
}

const totalPages = computed(() => Math.ceil(props.totalItems / props.itemsPerPage))

const isFirstPage = computed(() => props.currentPage === 1)

const isLastPage = computed(() => props.currentPage === totalPages.value)

const isCommerce = computed(() => props.appearance === 'commerce')

const goPrev = () => {
  if (props.currentPage > 1) {
    changePage(props.currentPage - 1)
  }
}

const goNext = () => {
  if (props.currentPage < totalPages.value) {
    changePage(props.currentPage + 1)
  }
}

const changePage = (page: number) => {
  router.push({
    query: {
      ...router.currentRoute.value.query,
      page: page.toString(),
    },
  })

  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  })

  emit('update:currentPage', page)
}

const paginationPages = computed<(number | string)[]>(() => {
  const pages: (number | string)[] = []
  const current = props.currentPage
  const total = totalPages.value

  if (total <= 1) {
    return [1]
  }

  if (total <= 5) {
    for (let i = 1; i <= total; i++) pages.push(i)
    return pages
  }

  if (current <= 3) {
    props.isAdmin ? pages.push(1, 2, 3) : pages.push(1, 2, 3)
    if (total > 4) pages.push('...')
    props.isAdmin ? pages.push(total - 1, total) : pages.push(total - 1, total)
  } else if (current >= total - 2) {
    pages.push(1, 2)
    pages.push('...')
    for (let i = total - 2; i <= total; i++) pages.push(i)
  } else {
    pages.push(1, 2)
    pages.push('...')
    pages.push(current - 1, current, current + 1)
    if (current + 2 < total) pages.push('...')
    pages.push(total)
  }

  return pages
})
</script>

<template>
      <div v-if="totalPages > 1" class="flex justify-center items-center mt-8 font-bold" :class="[
            isCommerce ? 'text-[#2626bf]' : 'text-[#2d5396]',
            isAdmin ? 'gap-2 text-sm' : 'gap-4 text-base'
      ]" dir="rtl">
            <div
                  :class="isAdmin ? ' flex items-center justify-center me-1 bg-white rounded-full w-8 h-8 admin-shadow' : ''">
                  <img :src="Chevron34" :width="isAdmin ? 20 : 26" alt="قبلی" class=""
                        :class="isFirstPage ? 'opacity-[0.4] cursor-not-allowed' : 'cursor-pointer'" @click="goPrev" />
            </div>
            <span v-for="(page, index) in paginationPages" :key="index"
                  class=" flex items-center justify-center min-w-8 px-2 h-8 "
                  @click="typeof page === 'number' && changePage(page)" :class="[
                        page === '...'
                              ? 'cursor-default text-gray-400 bg-transparent'
                              : '',

                        typeof page === 'number'
                              ? 'cursor-pointer'
                              : '',

                        typeof page === 'number' && isCommerce
                              ? ''
                              : '',

                        typeof page === 'number' && page === props.currentPage && isAdmin
                              ? `text-white ${isCommerce ? 'bg-[#2626bf] ' : 'bg-[#2d5396]'} rounded-lg`
                              : typeof page === 'number' && page === props.currentPage
                                    ? `text-white ${isCommerce ? 'bg-primary rounded-[10px]' : 'bg-[#2d5396]'} rounded-[5px]`
                                    : typeof page === 'number' && isAdmin
                                          ? 'bg-white text-black rounded-lg admin-shadow'
                                          : typeof page === 'number'
                                                ? ''
                                                : ''
                  ]">
                  {{ typeof page === 'number' ? fa(page) : page }}
            </span>
            <div
                  :class="isAdmin ? ' flex items-center justify-center ms-1 bg-white rounded-full w-8 h-8 admin-shadow' : ''">
                  <img :src="Chevron34" :width="isAdmin ? 20 : 26" alt="بعدی" class=" rotate-180"
                        :class="isLastPage ? 'opacity-[0.4] cursor-not-allowed' : 'cursor-pointer'" @click="goNext" />
            </div>
      </div>
</template>

<style>
.admin-shadow {
      box-shadow: 0 3px 1px -2px rgba(0, 0, 0, .2), 0 2px 2px 0 rgba(0, 0, 0, .14), 0 1px 5px 0 rgba(0, 0, 0, .12);
}
</style>